import { useState, useEffect, useRef, useCallback, createContext, useContext, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

const MAX_REFRESH_RETRIES = 5;
const BASE_DELAY_MS = 1000;

function getRetryDelay(attempt: number): number {
  const base = BASE_DELAY_MS * Math.pow(2, attempt); // 1s, 2s, 4s, 8s, 16s
  const jitter = base * 0.2 * (Math.random() * 2 - 1); // ±20%
  return Math.round(base + jitter);
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (password: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);
  const isRefreshingRef = useRef(false);

  // Retry session refresh with exponential backoff
  const retrySessionRefresh = useCallback(async () => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;

    while (retryCountRef.current < MAX_REFRESH_RETRIES) {
      const attempt = retryCountRef.current;
      const delay = getRetryDelay(attempt);
      if (import.meta.env.DEV) console.log(`[Auth] Session refresh retry ${attempt + 1}/${MAX_REFRESH_RETRIES} in ${delay}ms`);

      await new Promise(resolve => {
        retryTimeoutRef.current = setTimeout(resolve, delay);
      });

      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (!error && data.session) {
          if (import.meta.env.DEV) console.log("[Auth] Session refresh succeeded");
          setSession(data.session);
          setUser(data.session.user);
          retryCountRef.current = 0;
          isRefreshingRef.current = false;
          return;
        }
        if (import.meta.env.DEV) console.warn(`[Auth] Refresh attempt ${attempt + 1} failed:`, error?.message);
      } catch (err) {
        if (import.meta.env.DEV) console.warn(`[Auth] Refresh attempt ${attempt + 1} network error:`, err);
      }

      retryCountRef.current++;
    }

    if (import.meta.env.DEV) console.error("[Auth] All refresh retries exhausted — session lost");
    retryCountRef.current = 0;
    isRefreshingRef.current = false;
    // Only now accept the null session
    setSession(null);
    setUser(null);
  }, []);

  useEffect(() => {
    let initialSessionResolved = false;

    // 1. Restore session from storage FIRST (authoritative source)
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      initialSessionResolved = true;
      setLoading(false);
    });

    // 2. Listen for subsequent auth changes (sign in/out/token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        // Ignore events that arrive before getSession resolves
        if (!initialSessionResolved && event === "INITIAL_SESSION") {
          return;
        }

        // If we had a session and now it's gone, it might be a transient failure
        // Retry refresh before accepting the loss — unless user explicitly signed out
        if (event === "SIGNED_OUT") {
          // Explicit sign-out: accept immediately, no retry
          setSession(null);
          setUser(null);
          setLoading(false);
          return;
        }

        if (!newSession && session) {
          // Session disappeared unexpectedly — try to recover
          if (import.meta.env.DEV) console.warn("[Auth] Session lost unexpectedly, attempting recovery...");
          retrySessionRefresh();
          return;
        }

        setSession(newSession);
        setUser(newSession?.user ?? null);
        if (initialSessionResolved) {
          setLoading(false);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, [retrySessionRefresh]);

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error };
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    return { error };
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut, resetPassword, updatePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
